import {
  CreatePatientDocumentReplacementSchema,
  GeneratedPatientDocumentSchema,
  IssuePatientDocumentDraftSchema,
  LetterRenderResponseSchema,
  PatientDocumentDraftCommandSchema,
  PatientDocumentDraftSchema,
  PatientEducationResponseSchema,
  SavePatientDocumentDraftSchema,
} from "@emr/contracts";
import { z } from "zod";

export function createPatientDocumentsClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const message = data.error?.message ?? data.message;
      throw new Error(
        typeof message === "string" ? message : "The document request failed. Please retry.",
      );
    }
    return schema.parse(await response.json());
  }
  return {
    listGeneratedPatientDocuments: (
      patientId: string,
      encounterId?: string,
      signal?: AbortSignal,
    ) =>
      json(
        "/v1/patients/" +
          encodeURIComponent(patientId) +
          "/generated-documents" +
          (encounterId ? "?encounterId=" + encodeURIComponent(encounterId) : ""),
        z.array(GeneratedPatientDocumentSchema),
        undefined,
        signal,
      ),
    getGeneratedPatientDocument: (id: string, signal?: AbortSignal) =>
      json(
        "/v1/generated-documents/" + encodeURIComponent(id),
        GeneratedPatientDocumentSchema,
        undefined,
        signal,
      ),
    async downloadGeneratedPatientDocument(id: string, signal?: AbortSignal) {
      const response = await request(
        "/v1/generated-documents/" + encodeURIComponent(id) + "/pdf",
        undefined,
        signal,
      );
      if (!response.ok)
        throw new Error("The original PDF could not be retrieved or verified. Please retry.");
      return response.blob();
    },
    listPatientDocumentDrafts: (patientId: string, encounterId?: string, signal?: AbortSignal) =>
      json(
        "/v1/patients/" +
          encodeURIComponent(patientId) +
          "/document-drafts" +
          (encounterId ? "?encounterId=" + encodeURIComponent(encounterId) : ""),
        z.array(PatientDocumentDraftSchema),
        undefined,
        signal,
      ),
    createPatientDocumentReplacement: (
      id: string,
      body: z.input<typeof CreatePatientDocumentReplacementSchema>,
    ) =>
      json(
        "/v1/generated-documents/" + encodeURIComponent(id) + "/replacements",
        PatientDocumentDraftSchema,
        CreatePatientDocumentReplacementSchema.parse(body),
      ),
    savePatientDocumentDraft: (id: string, body: z.input<typeof SavePatientDocumentDraftSchema>) =>
      json(
        "/v1/document-drafts/" + encodeURIComponent(id) + "/save",
        PatientDocumentDraftSchema,
        SavePatientDocumentDraftSchema.parse(body),
      ),
    discardPatientDocumentDraft: (
      id: string,
      body: z.input<typeof PatientDocumentDraftCommandSchema>,
    ) =>
      json(
        "/v1/document-drafts/" + encodeURIComponent(id) + "/discard",
        PatientDocumentDraftSchema,
        PatientDocumentDraftCommandSchema.parse(body),
      ),
    previewPatientDocumentDraft: (
      id: string,
      body: z.input<typeof PatientDocumentDraftCommandSchema>,
    ) =>
      json(
        "/v1/document-drafts/" + encodeURIComponent(id) + "/preview",
        z.object({
          draft: PatientDocumentDraftSchema,
          encounterVersion: z.number().int().positive(),
          reviewContextHash: z.string(),
          document: z.union([LetterRenderResponseSchema, PatientEducationResponseSchema]),
        }),
        PatientDocumentDraftCommandSchema.parse(body),
      ),
    issuePatientDocumentDraft: (
      id: string,
      body: z.input<typeof IssuePatientDocumentDraftSchema>,
    ) =>
      json(
        "/v1/document-drafts/" + encodeURIComponent(id) + "/issue",
        GeneratedPatientDocumentSchema,
        IssuePatientDocumentDraftSchema.parse(body),
      ),
  };
}
export type PatientDocumentsApi = ReturnType<typeof createPatientDocumentsClient>;
